import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { listTenantsWithStats, TenantWithStats } from '../../services/tenants'
import {
  Buildings,
  MagnifyingGlass,
  Plus,
  Users,
  TrendUp,
  Briefcase,
  Pencil,
  Trash,
  Eye,
  CheckSquare,
  Square,
  DownloadSimple,
  ChartLine,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

export default function TenantsPage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const { showError } = useDialog()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPlan, setFilterPlan] = useState('all')
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])
  const [tenants, setTenants] = useState<TenantWithStats[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadTenants() {
      try {
        const data = await listTenantsWithStats()
        setTenants(data)
      } catch (error) {
        console.error('Error loading tenants:', error)
        showError('Erro', 'Erro ao carregar tenants')
      } finally {
        setLoading(false)
      }
    }
    loadTenants()
  }, [])

  // Calculate metrics from actual data
  const metrics = {
    overview: {
      total: tenants.length,
      active: tenants.length, // All tenants are active for now
      inactive: 0,
    },
    plans: {
      enterprise: 0,
      professional: 0,
      basic: tenants.length, // Default all to basic for now
    },
    growth: {
      thisMonth: tenants.filter(t => {
        const createdDate = new Date(t.createdAt)
        const now = new Date()
        return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear()
      }).length,
      totalCompanies: tenants.reduce((sum, t) => sum + (t.shippersCount || 0) + (t.freightForwardersCount || 0), 0),
      totalUsers: tenants.reduce((sum, t) => sum + (t.usersCount || 0), 0),
    },
  }

  // Filter functionality
  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase())

    // For now, all are active and basic plan
    const matchesStatus = filterStatus === 'all' || filterStatus === 'active'
    const matchesPlan = filterPlan === 'all' || filterPlan === 'Basic'

    return matchesSearch && matchesStatus && matchesPlan
  })

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedTenants.length === filteredTenants.length) {
      setSelectedTenants([])
    } else {
      setSelectedTenants(filteredTenants.map((t) => t.id))
    }
  }

  const toggleSelectTenant = (tenantId: string) => {
    setSelectedTenants((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    )
  }

  const isTenantSelected = (tenantId: string) => selectedTenants.includes(tenantId)
  const isAllSelected =
    filteredTenants.length > 0 && selectedTenants.length === filteredTenants.length

  // Get plan badge
  const getPlanBadge = (plan: string) => {
    const badges = {
      Enterprise: 'bg-violet-100 text-violet-700',
      Professional: 'bg-blue-100 text-blue-700',
      Basic: 'bg-gray-100 text-gray-700',
    }
    return badges[plan as keyof typeof badges] || 'bg-gray-100 text-gray-700'
  }

  // Get status indicator
  const getStatusIndicator = (lastActivity: string) => {
    const daysSinceActivity = Math.floor(
      (new Date().getTime() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
    )

    if (daysSinceActivity <= 7) {
      return <div className="w-2 h-2 rounded-full bg-emerald-500" title="Ativo (<7 dias)" />
    }
    if (daysSinceActivity <= 30) {
      return <div className="w-2 h-2 rounded-full bg-amber-500" title="Moderado (7-30 dias)" />
    }
    return <div className="w-2 h-2 rounded-full bg-red-500" title="Inativo (>30 dias)" />
  }

  // Get initials
  const getInitials = (name: string) => {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando tenants...</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Gestão de Tenants
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedTenants.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedTenants.length}</span>
                  selecionado{selectedTenants.length > 1 ? 's' : ''}
                </div>
              )}
              <Button
                onClick={() => navigate('/master/tenants/new')}
                size="default"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus size={18} weight="bold" />
                Novo Tenant
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Zone - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Overview Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Tenants Overview
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{metrics.overview.total}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {metrics.overview.active} ativos, {metrics.overview.inactive} inativos
                </p>
              </div>
              <Buildings size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Subscription Plans Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Planos de Assinatura
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Enterprise</span>
                <span className="font-semibold text-gray-900">{metrics.plans.enterprise}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Professional</span>
                <span className="font-semibold text-gray-900">{metrics.plans.professional}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Basic</span>
                <span className="font-semibold text-gray-900">{metrics.plans.basic}</span>
              </div>
            </div>
          </div>

          {/* Growth Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Crescimento
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">+{metrics.growth.thisMonth}</p>
                <p className="text-sm text-gray-600 mt-1">Novos este mês</p>
              </div>
              <div className="text-right text-sm text-gray-600">
                <div className="flex items-center gap-1 mb-1">
                  <Briefcase size={14} className="text-gray-500" />
                  <span>{metrics.growth.totalCompanies} empresas</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users size={14} className="text-gray-500" />
                  <span>{metrics.growth.totalUsers} usuários</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por nome ou slug do tenant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>

            {/* Plan Filter */}
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos os Planos</option>
              <option value="Enterprise">Enterprise</option>
              <option value="Professional">Professional</option>
              <option value="Basic">Basic</option>
            </select>

            {/* Export */}
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <DownloadSimple size={16} />
              Exportar
            </Button>
          </div>
        </div>

        {/* Data Grid */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center justify-center w-full"
                    >
                      {isAllSelected ? (
                        <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                      ) : (
                        <Square size={18} className="text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresas
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuários
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Processos
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Em andamento
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Concluídos
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      isTenantSelected(tenant.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectTenant(tenant.id)}
                        className="flex items-center justify-center"
                      >
                        {isTenantSelected(tenant.id) ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-emerald-700">
                            {getInitials(tenant.name)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {tenant.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">{tenant.id.slice(0, 8)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadge('Basic')}`}>
                        Basic
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-gray-900">
                        {(tenant.shippersCount || 0) + (tenant.freightForwardersCount || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-gray-900">
                        {tenant.usersCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-semibold text-gray-900">
                        {tenant.processesTotal || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-amber-600">
                        {tenant.processesInProgress || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-emerald-600">
                        {tenant.processesCompleted || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIndicator(tenant.updatedAt)}
                        <span className="text-xs font-medium text-emerald-700">
                          Ativo
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {new Date(tenant.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/master/tenants/${tenant.id}`)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye size={16} className="text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/master/tenants/${tenant.id}/edit`)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil size={16} className="text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTenants.length === 0 && (
            <div className="text-center py-12 bg-gray-50">
              <Buildings size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
              <p className="text-gray-500 text-sm">Nenhum tenant encontrado</p>
            </div>
          )}

          {/* Footer with pagination */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{filteredTenants.length}</span> tenants
              {selectedTenants.length > 0 && (
                <>
                  {' '}
                  · <span className="font-medium">{selectedTenants.length}</span> selecionado
                  {selectedTenants.length > 1 ? 's' : ''}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select className="px-2 py-1 border border-gray-200 rounded text-sm bg-white">
                <option>50 por página</option>
                <option>100 por página</option>
                <option>200 por página</option>
              </select>
              <span className="text-sm text-gray-600">Página 1 de 1</span>
            </div>
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
